// EMCON-ordren: nivået som gjelder, hvem som satte det, og hvorfor.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// `emcon.ts` er policy-MOTOREN — den svarer på «er denne klassen tillatt under
// dette nivået». Den har vært skrevet, testet og **hatt null kallsteder**
// (verifisert 2026-08-18). Motoren manglet altså to ting for å bli en funksjon:
// et sted å bo (hvilket nivå gjelder NÅ, for hvem), og en vei ut til dem som
// skal etterleve det.
//
// ⚠ EMCON HÅNDHEVES ANNERLEDES ENN RBAC, OG FORSKJELLEN ER VIKTIG.
// Grunnloven §4 sier at tilgangskontroll håndheves autoritativt i backend, og at
// klient-gating aldri er nok. Det er riktig for TILGANG — der er trusselen en
// klient som lyver. EMCON er noe annet: det som skal kontrolleres er **senderen i
// telefonen**. Hubben kan ikke hindre et apparat fra å stråle. Derfor:
//
//   • Hubben er ENESTE SANNHET om NIVÅET (RBAC-gated, revidert, persistert).
//   • Hubben begrenser sin EGEN nedstrøms-trafikk, fordi hver push vekker radioen
//     og koster utstråling på svarveien.
//   • KLIENTEN er der etterlevelsen faktisk skjer. Det er ikke en svakhet i
//     modellen — det er hvor fenomenet bor.
//
// Å behandle EMCON som RBAC («hubben stopper det, klienten trenger ikke bry seg»)
// ville gitt radiotaushet på papiret og en telefon som fortsatt sender BFT hvert
// femte minutt.
import { z } from 'zod';
import { TrackOrgSchema } from './track.js';
export const EmconLevelSchema = z.enum(['alpha', 'bravo', 'charlie', 'delta']);
/**
 * En stående EMCON-ordre.
 *
 * ⚠ `satAv` OG `grunn` ER IKKE METADATA. Radiotaushet lammer en avdelings evne
 * til å rapportere; den som pålegger den skal kunne identifiseres, og den som
 * etterlever den skal kunne lese hvorfor. En EMCON-ordre uten avsender er en
 * ordre ingen har gitt, og den blir ikke fulgt.
 */
export const EmconOrdreSchema = z.object({
    nivaa: EmconLevelSchema,
    /** Hvem som satte den (autentisert bruker-id, stemplet server-side). */
    satAv: z.string().min(1).max(200),
    satMs: z.number().int(),
    /** Hvorfor. Kort, og den vises til styrken. */
    grunn: z.string().max(500).default(''),
    /**
     * Når ordren opphører av seg selv (ms). Udefinert = står til den endres.
     *
     * ⚠ ET TIDSVINDU ER TRYGGERE ENN EN STÅENDE ORDRE. Glemt radiotaushet er en
     * avdeling som ikke kan rapportere når det gjelder — og den som satte den er
     * kanskje av vakt. Med `gjelderTilMs` faller nivået tilbake av seg selv.
     */
    gjelderTilMs: z.number().int().optional(),
    /** Hvilken org ordren gjelder for. Udefinert = hele hubbens distrikt. */
    org: TrackOrgSchema.optional(),
});
/** Normal drift — det nivået som gjelder når ingen har pålagt noe. */
export const EMCON_STANDARD = 'delta';
/**
 * Nivået som gjelder nå, gitt en ordre som kan være utløpt.
 *
 * ⚠ EN UTLØPT ORDRE FALLER TIL `delta`, IKKE TIL FORRIGE NIVÅ. Å «gå tilbake til
 * det som gjaldt før» krever en historikk vi ikke har, og ville gitt et nivå
 * ingen har bestemt. Normal drift er det ærlige utgangspunktet.
 */
export function gjeldendeNivaa(ordre, naa) {
    if (!ordre)
        return EMCON_STANDARD;
    if (ordre.gjelderTilMs !== undefined && ordre.gjelderTilMs <= naa)
        return EMCON_STANDARD;
    return ordre.nivaa;
}
/** Er ordren fortsatt i kraft? */
export function ordreGjelder(ordre, naa) {
    if (!ordre)
        return false;
    return ordre.gjelderTilMs === undefined || ordre.gjelderTilMs > naa;
}
/** Gjenstående tid som tekst — til statuslinja i COP og GNÅ. */
export function emconGjenstaarTekst(ordre, naa) {
    if (!ordre)
        return 'Ingen EMCON-ordre — normal drift';
    if (ordre.gjelderTilMs === undefined)
        return 'Står til den endres';
    const igjen = ordre.gjelderTilMs - naa;
    if (igjen <= 0)
        return 'Utløpt — normal drift';
    const min = Math.round(igjen / 60_000);
    if (min < 90)
        return `Gjelder ${min} min til`;
    const t = Math.round(igjen / 3_600_000);
    return t < 48 ? `Gjelder ${t} t til` : `Gjelder ${Math.round(igjen / 86_400_000)} døgn til`;
}
/**
 * Hvilke `kind`-verdier hubben ikke skal dytte nedstrøms under et nivå.
 *
 * ⚠ DETTE ER HUBBENS EGEN DISIPLIN, IKKE EN TILGANGSKONTROLL. Hver melding vi
 * pusher til en telefon vekker radioen, og de fleste klienter svarer med noe —
 * en kvittering, en lesebekreftelse, en synk. Under radiotaushet er den
 * SVARTRAFIKKEN utstrålingen vi ville unngå. Derfor holder hubben tilbake det
 * efemere selv, i stedet for å sende det og håpe klienten er stille.
 */
export const EMCON_NEDSTROMS_STILLE = {
    alpha: ['pointer', 'draw', 'chat', 'report', 'intel', 'field', 'units', 'calendar'],
    bravo: ['pointer', 'draw', 'chat', 'report', 'intel', 'field'],
    charlie: ['pointer', 'draw', 'chat'],
    delta: [],
};
/** Skal hubben holde tilbake denne `kind` nedstrøms under nivået? */
export function holdTilbakeNedstroms(nivaa, kind) {
    return EMCON_NEDSTROMS_STILLE[nivaa].includes(kind);
}
//# sourceMappingURL=emcon-ordre.js.map